import React, { useState, useEffect } from "react";
import { ThemeId, THEMES } from "../themes/tokens";
import { applyTheme } from "../themes/ThemeManager";
import { themeService } from "../services/theme.service";
import { socketClient } from "../socket/socket.client";

interface ThemePickerProps {
  onClose?: () => void;
}

export function ThemePicker({ onClose }: ThemePickerProps) {
  const [selectedTheme, setSelectedTheme] = useState<ThemeId>("classic-dark");
  const [savedTheme, setSavedTheme] = useState<ThemeId>("classic-dark");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load current theme on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const currentTheme = await themeService.getUserTheme();
        setSelectedTheme(currentTheme);
        setSavedTheme(currentTheme);
        // Apply theme immediately for preview
        applyTheme(currentTheme);
      } catch (err) {
        console.error("[ThemePicker] Error loading theme:", err);
      }
    };
    loadTheme();
  }, []);

  const handleThemeSelect = (themeId: ThemeId) => {
    setSelectedTheme(themeId);
    // Apply theme instantly for preview (not saved yet)
    applyTheme(themeId);
    setError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    
    try {
      const success = await themeService.updateUserTheme(selectedTheme);
      if (success) {
        setSavedTheme(selectedTheme);
        // Theme is already applied, notify other tabs via socket
        socketClient.updateUserTheme(selectedTheme);
        onClose?.();
      } else {
        setError("Failed to save theme. Please try again.");
        // Revert to saved theme on error
        applyTheme(savedTheme);
        setSelectedTheme(savedTheme);
      }
    } catch (err) {
      console.error("[ThemePicker] Error saving theme:", err);
      setError("Failed to save theme. Please try again.");
      // Revert to saved theme on error
      applyTheme(savedTheme);
      setSelectedTheme(savedTheme);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Revert to saved theme
    applyTheme(savedTheme);
    setSelectedTheme(savedTheme);
    setError(null);
    onClose?.();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>🎨 Choose Theme</h3>
        {onClose && (
          <button onClick={handleCancel} style={styles.closeButton}>
            ✕
          </button>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.themesGrid}>
        {(Object.keys(THEMES) as ThemeId[]).map((themeId) => {
          const theme = THEMES[themeId];
          const isSelected = selectedTheme === themeId;
          
          return (
            <button
              key={themeId}
              onClick={() => handleThemeSelect(themeId)}
              style={{
                ...styles.themeCard,
                ...(isSelected ? styles.themeCardSelected : {}),
              }}
            >
              <div style={styles.themePreview}>
                {/* Preview colors */}
                <div
                  style={{
                    ...styles.colorSwatch,
                    background: theme["--bg"],
                  }}
                />
                <div style={styles.colorRow}>
                  <div
                    style={{
                      ...styles.colorDot,
                      background: theme["--card-red"],
                    }}
                  />
                  <div
                    style={{
                      ...styles.colorDot,
                      background: theme["--card-yellow"],
                    }}
                  />
                  <div
                    style={{
                      ...styles.colorDot,
                      background: theme["--card-green"],
                    }}
                  />
                  <div
                    style={{
                      ...styles.colorDot,
                      background: theme["--card-blue"],
                    }}
                  />
                </div>
              </div>
              <div style={styles.themeName}>
                {themeId
                  .split("-")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
              </div>
              {isSelected && (
                <div style={styles.checkmark}>✓</div>
              )}
            </button>
          );
        })}
      </div>

      <div style={styles.actions}>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          style={styles.saveButton}
          disabled={isSaving || selectedTheme === savedTheme}
        >
          {isSaving ? "Saving..." : "Save Theme"}
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: "20px 0",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "600",
    color: "var(--text)",
  },
  closeButton: {
    background: "var(--bg-card-hover)",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "var(--text)",
    padding: "0",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
  },
  error: {
    padding: "10px",
    background: "rgba(231, 76, 60, 0.2)",
    color: "#ffcccc",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "14px",
    border: "1px solid var(--card-red)",
  },
  themesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "15px",
    marginBottom: "20px",
  },
  themeCard: {
    position: "relative",
    padding: "15px",
    background: "var(--bg-card-hover)",
    border: "2px solid var(--border-subtle)",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },
  themeCardSelected: {
    border: "2px solid var(--accent)",
    background: "var(--bg-card)",
    boxShadow: "0 0 0 2px var(--accent)",
  },
  themePreview: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  colorSwatch: {
    width: "100%",
    height: "40px",
    borderRadius: "6px",
    border: "1px solid var(--border-subtle)",
  },
  colorRow: {
    display: "flex",
    gap: "6px",
    justifyContent: "center",
  },
  colorDot: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    border: "1px solid var(--border-subtle)",
  },
  themeName: {
    fontSize: "14px",
    fontWeight: "500",
    color: "var(--text)",
    textAlign: "center",
  },
  checkmark: {
    position: "absolute",
    top: "8px",
    right: "8px",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background: "var(--accent)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "bold",
  },
  actions: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
  },
  cancelButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    color: "var(--text)",
    background: "var(--bg-card-hover)",
    border: "2px solid var(--border-medium)",
    borderRadius: "6px",
    cursor: "pointer",
  },
  saveButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    background: "var(--accent)",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
};
